import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw, CheckCircle2, XCircle, HelpCircle, Clock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from '../components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_URL = `${BACKEND_URL}/api`;

// 18–19 Ekim 2026 geriye dönük testlerinin sade özeti (her vaka yalnızca o gün
// bilinen veriyle çalıştırıldı; ölçü: aynı dönemde XU100'den iyi gidip gitmediği).
const LEARNINGS = [
  {
    verdict: 'no',
    title: 'Geçmişte benzer grafikler',
    text: 'Benzer grafiklerin devamı, hissenin kendi devamını tahmin etmedi. "İyi" ve "kötü" denen hisselerin ikisi de yaklaşık yarı yarıya piyasayı geçti — yazı-tura ile aynı.',
    sample: '2.394 geçmiş test',
  },
  {
    verdict: 'no',
    title: 'Yükselecek Hisse Bul',
    text: 'Seçilen hisseler, aynı tarihteki diğer hisselerle neredeyse aynı sonucu verdi. Yükselişten önce gelen kalıplarla düşüşten önce gelenler ayırt edilemedi.',
    sample: '650 geçmiş test',
  },
  {
    verdict: 'no',
    title: 'Saatlik momentum listesi',
    text: 'Son saatlerde en çok hareketlenen hisseler, sonraki saat, gün ve haftada diğerlerinden iyi gitmedi (%47–49). 1 saatlik ufukta hafif geri verme var.',
    sample: '589 hisse, 2 yıllık saatlik veri',
  },
  {
    verdict: 'no',
    title: 'Mal toplama (Wyckoff: sessiz alım, spring, toplama kurulumu)',
    text: 'Toplama kurulumu gösteren hisseler de, "spring" yapan hisseler de piyasayla aynı sonucu verdi (%49–50).',
    sample: '3.300 + 5.700 olay, 2017–2026',
  },
  {
    verdict: 'maybe',
    title: 'Göreli güç (son 6 ayda piyasaya göre performans)',
    text: 'İlk testler olumlu görünmüştü ama her tarihte tüm hisseleri birlikte kıyaslayan temiz testte her yıl sıfıra yakın çıktı. Canlı takipte.',
    sample: '2017–2026, her yıl ayrı ayrı',
  },
  {
    verdict: 'yes',
    title: 'Sakin ve dengeli hisseler (düşük oynaklık + topolojik denge)',
    text: 'Sakin seyreden ve grafiği tek bir baskın hareket yerine dengeli dip/tepelerden oluşan hisseler sonraki 3–6 ayda daha iyi gitti; en belirgin fark, oynak ve tek yönlü hisselerin geride kalması. Topolojik ölçü oynaklıktan bağımsız ek bilgi taşıyor.',
    sample: 'Günlük veride 10 yılın 8–9\'unda olumlu (2017–2026); saatlik veride de doğrulandı (en üst %10, %55–56)',
  },
  {
    verdict: 'maybe',
    title: 'Piyasa uyarısı (sayfa üstündeki piyasa durumu)',
    text: 'Geçmişte büyük düşüşlerden önce uyarı verdi (2018 kur krizi ve Eylül 2026 dahil), ama yalnızca 6 olay var. Umut verici; canlı takipte.',
    sample: '2017–2026, 6 olay',
  },
];

const VERDICT_STYLE = {
  yes: { icon: CheckCircle2, color: 'text-[#6D7C3B]', label: 'İşe yarıyor' },
  no: { icon: XCircle, color: 'text-[#B04832]', label: 'İşe yaramıyor' },
  maybe: { icon: HelpCircle, color: 'text-[#C86F4A]', label: 'Henüz belli değil' },
  wait: { icon: Clock, color: 'text-[#7A6A5C]', label: 'Sonuç bekleniyor' },
};

// Canlı takip edilen sinyaller ve sade açıklamaları
const TRACKED = [
  {
    kinds: ['simons_h_top', 'simons_h_bottom'],
    pair: { name: 'simons_h', topLabel: 'saatlik Simons en yüksek 30', bottomLabel: 'en düşük 30' },
    title: 'Simons Saatlik (gölge, 1 haftalık)',
    what: 'Saatlik barlarla çalışan deneme modeli. Her hafta en yüksek ve en düşük skorlu 30\'ar hisse kaydedilir ve 1 hafta sonra ölçülür. Gerçek sonuçta kendini kanıtlarsa öne çıkarılacak; kanıtlamazsa kaldırılacak.',
    horizons: ['1w'],
  },
  {
    kinds: ['simons_top', 'simons_bottom'],
    pair: { name: 'simons', topLabel: 'Simons skoru en yüksek 30', bottomLabel: 'en düşük 30' },
    title: 'Simons Skoru (öğrenen model)',
    what: 'Her hafta, mal toplama + geometri + topoloji modelinin en yüksek ve en düşük skor verdiği 30\'ar hisse kaydedilir. İşe yarıyorsa ilk grup piyasayı ikinciden belirgin şekilde daha sık geçmeli.',
  },
  {
    kinds: ['calm_top', 'calm_bottom'],
    pair: { name: 'calm', topLabel: 'en sakin-dengeli 30', bottomLabel: 'en oynak-tek yönlü 30' },
    title: 'Sakin ve dengeli hisseler',
    what: 'Her hafta, düşük oynaklık ve topolojik denge skoruna göre en iyi ve en kötü 30\'ar likit hisse kaydedilir. İşe yarıyorsa ilk grup piyasayı ikinciden daha sık geçmeli.',
  },
  {
    kinds: ['momentum_top', 'momentum_bottom'],
    pair: { name: 'momentum', topLabel: 'en güçlü 20', bottomLabel: 'en zayıf 20' },
    title: 'Göreli güç: en güçlü ve en zayıf 20 hisse',
    what: 'Her hafta, son 6 ayda piyasadan en güçlü ve en zayıf giden 20\'şer hisse kaydedilir. İşe yarıyorsa güçlüler piyasayı zayıflardan daha sık geçmeli.',
  },
  {
    kinds: ['market_state'],
    title: 'Piyasa durumu',
    what: 'Her hafta piyasanın genel durumu ve risk uyarısı kaydedilir. İşe yarıyorsa uyarıdan sonraki haftalarda piyasa daha zayıf gitmeli.',
  },
  {
    kinds: ['similar_scan'],
    title: 'Geçmişte benzer grafikler taramaları',
    what: 'Bugünkü tarihle yapılan her tarama ve özetin söylediği kaydedilir.',
  },
  {
    kinds: ['partial_pick'],
    title: 'Yükselecek Hisse Bul seçimleri',
    what: 'Bu moddaki her taramanın seçtiği hisseler kaydedilir. İşe yarıyorsa seçilenler piyasayı yarıdan fazla geçmeli.',
  },
];

const MIN_RESULTS_FOR_VERDICT = 30;

const KIND_LABELS = {
  simons_h_top: 'Simons saatlik (gölge): en yüksek 30',
  simons_h_bottom: 'Simons saatlik (gölge): en düşük 30',
  simons_c_top: 'Simons aday (gölge): en yüksek 30',
  simons_c_bottom: 'Simons aday (gölge): en düşük 30',
  simons_top: 'Simons skoru: en yüksek 30',
  simons_bottom: 'Simons skoru: en düşük 30',
  calm_top: 'Sakin-dengeli: en iyi 30',
  calm_bottom: 'Sakin-dengeli: en kötü 30',
  momentum_top: 'Göreli güç: en güçlü 20',
  momentum_bottom: 'Göreli güç: en zayıf 20',
  market_state: 'Piyasa durumu',
  similar_scan: 'Geçmişte benzer grafikler',
  partial_pick: 'Yükselecek Hisse Bul',
};
const HORIZON_LABELS = { '1w': '1 hafta', '4w': '4 hafta', '13w': '13 hafta', '26w': '26 hafta' };

const pct = (value) => (typeof value === 'number' && Number.isFinite(value) ? `%${Math.round(value)}` : '—');
const addDays = (iso, days) => new Date(new Date(iso).getTime() + days * 86400000).toLocaleDateString('tr-TR');

// Bir sinyal için canlı sonuçların sade cümlesi ve hükmü
const describeLive = (tracked, totals, kinds, spreads) => {
  const recorded = tracked.kinds.reduce((sum, k) => sum + (totals[k]?.forward || 0), 0);
  const firstForward = tracked.kinds.map((k) => totals[k]?.first_forward).filter(Boolean).sort()[0];
  if (!recorded) {
    return { verdict: 'wait', lines: ['Henüz ileriye dönük kayıt yok.'] };
  }
  const horizons = tracked.horizons || ['13w', '4w'];
  const horizon = horizons.find((h) => tracked.kinds.some((k) => kinds[k]?.[h]?.n));
  if (!horizon) {
    return {
      verdict: 'wait',
      lines: [
        `${recorded} kayıt toplandı.`,
        firstForward
          ? (horizons[0] === '1w'
            ? `İlk sonuçlar ${addDays(firstForward, 7)} civarında gelecek (1 hafta sonra).`
            : `İlk sonuçlar ${addDays(firstForward, 28)} civarında gelecek (4 hafta sonra).`)
          : '',
      ].filter(Boolean),
    };
  }
  const horizonText = { '13w': '3 ay', '4w': '4 hafta', '1w': '1 hafta' }[horizon] || horizon;
  if (tracked.pair) {
    const [topKind, bottomKind] = tracked.kinds;
    const top = kinds[topKind]?.[horizon];
    const bottom = kinds[bottomKind]?.[horizon];
    const n = (top?.n || 0) + (bottom?.n || 0);
    const lines = [
      `${horizonText} sonra: ${tracked.pair.topLabel} hissenin ${pct(top?.beat_index_rate)}'i, ${tracked.pair.bottomLabel} hissenin ${pct(bottom?.beat_index_rate)}'i piyasayı geçti.`,
    ];
    const spread = spreads?.[tracked.pair.name]?.[horizon];
    if (spread) lines.push(`${spread.weeks} haftanın ${pct(spread.positive_weeks_rate)}'inde ilk grup ikinciden iyi gitti.`);
    let verdict = 'maybe';
    if (n >= MIN_RESULTS_FOR_VERDICT * 2 && typeof top?.beat_index_rate === 'number' && typeof bottom?.beat_index_rate === 'number') {
      const gap = top.beat_index_rate - bottom.beat_index_rate;
      verdict = gap >= 8 ? 'yes' : gap <= 0 ? 'no' : 'maybe';
    }
    return { verdict, lines };
  }
  if (tracked.kinds.includes('market_state')) {
    const s = kinds.market_state?.[horizon];
    const lines = [`${s?.n || 0} haftalık sonuç ölçüldü; ${s?.warnings || 0} haftada uyarı vardı.`];
    if (s?.warnings) {
      const fmt = (v) => (typeof v === 'number' ? `${v > 0 ? '+' : ''}${v.toFixed(1)}%` : '—');
      lines.push(`Uyarıdan ${horizonText} sonra piyasa ${fmt(s.index_return_after_warning)}, uyarı yokken ${fmt(s.index_return_no_warning)} değişti (medyan).`);
    }
    return { verdict: s?.warnings >= 3 ? 'maybe' : 'wait', lines };
  }
  const s = tracked.kinds.map((k) => kinds[k]?.[horizon]).find(Boolean);
  const lines = [`${horizonText} sonra: ${s.n} sonucun ${pct(s.beat_index_rate)}'i piyasayı geçti (şans eseri beklenen yaklaşık %50).`];
  let verdict = 'maybe';
  if (s.n >= MIN_RESULTS_FOR_VERDICT && typeof s.beat_index_rate === 'number') {
    verdict = s.beat_index_rate >= 56 ? 'yes' : s.beat_index_rate <= 50 ? 'no' : 'maybe';
  } else {
    lines.push(`Güvenilir bir hüküm için en az ${MIN_RESULTS_FOR_VERDICT} sonuç gerekiyor.`);
  }
  return { verdict, lines };
};

const VerdictBadge = ({ verdict }) => {
  const style = VERDICT_STYLE[verdict] || VERDICT_STYLE.wait;
  const Icon = style.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${style.color}`}>
      <Icon className="w-4 h-4" />
      {style.label}
    </span>
  );
};

const SignalScorecardPage = () => {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [card, setCard] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const fetchScorecard = async () => {
    setLoading(true);
    try {
      const [res, fb] = await Promise.all([
        axios.get(`${API_URL}/admin/signal-scorecard`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/admin/similarity-feedback`, { headers: getAuthHeader() }).catch(() => null),
      ]);
      setCard(res.data || null);
      setFeedback(fb?.data || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sinyal karnesi yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScorecard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = card?.totals || {};
  const kinds = card?.kinds || {};
  const spreads = card?.spreads || { momentum: card?.momentum_spread || {} };

  return (
    <div className="min-h-screen bg-[#F6F1EA]" data-testid="signal-scorecard-page">
      <Sidebar />
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-3xl font-bold text-[#2E2620] font-['Playfair_Display']">Sinyal Karnesi</h1>
              <p className="text-[#7A6A5C] mt-2 max-w-2xl leading-relaxed">
                Bu sayfa, sistemin önerilerinin sonradan doğru çıkıp çıkmadığını gösterir. Ölçü basit: önerilen hisse,
                aynı dönemde Borsa İstanbul'un genelinden (XU100) daha iyi gitti mi? Şans eseri bu oran yaklaşık %50'dir;
                işe yarayan bir sinyal bunun belirgin şekilde üstünde olmalı.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={fetchScorecard}
              disabled={loading}
              className="border-[#2E2620]/20 text-[#2E2620] rounded-full"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Yenile
            </Button>
          </div>

          <Card className="card-organic">
            <CardHeader>
              <CardTitle className="font-['Playfair_Display'] text-[#2E2620]">Şu ana kadar ne öğrendik?</CardTitle>
              <p className="text-sm text-[#7A6A5C]">
                Sistemi geçmiş tarihlere götürüp, o gün bilinen bilgiyle ne diyeceğini ve sonra ne olduğunu karşılaştırdık.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {LEARNINGS.map((item) => (
                <div key={item.title} className="border-b border-[#E6DCCF]/70 pb-4 last:border-0 last:pb-0">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="font-medium text-[#2E2620]">{item.title}</p>
                    <VerdictBadge verdict={item.verdict} />
                  </div>
                  <p className="text-sm text-[#7A6A5C] mt-1 leading-relaxed">{item.text}</p>
                  <p className="text-xs text-[#7A6A5C]/80 mt-1">{item.sample}</p>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="card-organic">
            <CardHeader>
              <CardTitle className="font-['Playfair_Display'] text-[#2E2620]">Canlı takip</CardTitle>
              <p className="text-sm text-[#7A6A5C]">
                Geçmiş testler insanı yanıltabilir; asıl kanıt bundan sonra gelecek. Her öneri verildiği anda kilitlenir
                ve sonucu her gece otomatik ölçülür.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {loading && !card ? (
                <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-[#C86F4A]" /></div>
              ) : (
                TRACKED.map((tracked) => {
                  const live = describeLive(tracked, totals, kinds, spreads);
                  return (
                    <div key={tracked.title} className="border-b border-[#E6DCCF]/70 pb-4 last:border-0 last:pb-0">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <p className="font-medium text-[#2E2620]">{tracked.title}</p>
                        <VerdictBadge verdict={live.verdict} />
                      </div>
                      <p className="text-xs text-[#7A6A5C] mt-1 leading-relaxed">{tracked.what}</p>
                      {live.lines.map((line) => (
                        <p key={line} className="text-sm text-[#2E2620] mt-1">{line}</p>
                      ))}
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>

          <Card className="card-organic" data-testid="similarity-feedback-card">
            <CardHeader>
              <CardTitle className="font-['Playfair_Display'] text-[#2E2620]">Benzerlik geri bildirimi</CardTitle>
              <p className="text-sm text-[#7A6A5C]">
                Analiz sayfasındaki "Karşılaştır" penceresinde verilen "Benziyor / Benzemiyor" oyları. Sistemin benzerlik
                puanının göz kararıyla ne kadar uyuştuğunu gösterir.
              </p>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {!feedback || !feedback.total ? (
                <p className="text-[#7A6A5C]">
                  Henüz oy yok. Bir tarama yapıp sonuçlardan birinde "Karşılaştır"a basın ve iki grafiğe bakarak oy verin.
                </p>
              ) : (
                <>
                  <p className="text-[#2E2620]">
                    {feedback.total} oy · gösterilen eşleşmelerin <strong>%{Math.round(feedback.similar_rate)}</strong>'i "benziyor" bulundu.
                  </p>
                  {typeof feedback.agreement?.blended === 'number' && (() => {
                    const value = Math.round(feedback.agreement.blended * 100);
                    const label = value >= 75 ? 'iyi' : value >= 60 ? 'orta' : 'zayıf';
                    const parts = [['benzerlik', 'similarity'], ['korelasyon', 'correlation'], ['bar yönü (şekil)', 'shape']]
                      .filter(([, k]) => typeof feedback.agreement[k] === 'number')
                      .map(([name, k]) => `${name} %${Math.round(feedback.agreement[k] * 100)}`);
                    return (
                      <p className="text-[#2E2620]">
                        Sistemin skoru, sizin "benziyor" dediklerinizi "benzemiyor"lardan <strong>%{value}</strong> oranında
                        doğru ayırıyor (<strong>{label}</strong>; %50 = hiç, %100 = mükemmel).
                        {parts.length > 0 && <span className="text-[#7A6A5C]"> Bileşenler: {parts.join(' · ')}.</span>}
                      </p>
                    );
                  })()}
                  {Array.isArray(feedback.by_score_bucket) && (
                    <div className="text-[#7A6A5C]">
                      {feedback.by_score_bucket.map((b) => (
                        <p key={b.range}>Skor {b.range}: %{Math.round(b.similar_rate)} "benziyor" ({b.n} oy)</p>
                      ))}
                    </div>
                  )}
                  {feedback.total < 100 && (
                    <p className="text-xs text-[#7A6A5C]">Güvenilir bir yorum için en az 100 oy önerilir.</p>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <div>
            <button
              type="button"
              onClick={() => setShowDetails((v) => !v)}
              className="text-sm text-[#7A6A5C] underline underline-offset-4"
            >
              {showDetails ? 'Teknik ayrıntıları gizle' : 'Teknik ayrıntıları göster'}
            </button>
            {showDetails && (
              <div className="mt-3 p-3 rounded-lg bg-white border border-[#E6DCCF] overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[#7A6A5C] border-b border-[#E6DCCF]">
                      <th className="py-1.5 pr-3 font-medium">Sinyal</th>
                      <th className="py-1.5 pr-3 font-medium">Kayıt (ileriye dönük)</th>
                      <th className="py-1.5 pr-3 font-medium">Ufuk</th>
                      <th className="py-1.5 pr-3 font-medium">Sonuç sayısı</th>
                      <th className="py-1.5 pr-3 font-medium">Piyasayı geçen</th>
                      <th className="py-1.5 pr-3 font-medium">Medyan fark</th>
                      <th className="py-1.5 pr-3 font-medium">Ortalama fark</th>
                      <th className="py-1.5 font-medium">Tahmin–sonuç IC</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(totals).map(([kind, t]) => {
                      const byHorizon = kinds[kind] || {};
                      const horizons = Object.keys(byHorizon).length ? Object.keys(byHorizon) : ['—'];
                      return horizons.map((h, i) => {
                        const s = byHorizon[h] || {};
                        const num = (v, suffix = '') => (typeof v === 'number' ? `${v > 0 && suffix !== '%' ? '+' : ''}${v}${suffix}` : '—');
                        return (
                          <tr key={`${kind}-${h}`} className="border-b border-[#E6DCCF]/60 text-[#2E2620]">
                            <td className="py-1.5 pr-3">{i === 0 ? (KIND_LABELS[kind] || kind) : ''}</td>
                            <td className="py-1.5 pr-3">{i === 0 ? `${t.total} (${t.forward})` : ''}</td>
                            <td className="py-1.5 pr-3">{HORIZON_LABELS[h] || h}</td>
                            <td className="py-1.5 pr-3">{s.n ?? '—'}</td>
                            <td className="py-1.5 pr-3">{num(s.beat_index_rate, '%')}</td>
                            <td className="py-1.5 pr-3">{num(s.median_excess, ' puan')}</td>
                            <td className="py-1.5 pr-3">{num(s.mean_excess, ' puan')}</td>
                            <td className="py-1.5">{num(s.ic_predicted_vs_realized)}</td>
                          </tr>
                        );
                      });
                    })}
                  </tbody>
                </table>
                <p className="text-[11px] text-[#7A6A5C] mt-2">
                  Fark: hissenin getirisi eksi aynı dönemde XU100 getirisi (puan). IC: tarama özetinin tahmini ile gerçekleşen
                  arasındaki sıralama korelasyonu (0 = ilişki yok). Veri: {card?.generated_at ? new Date(card.generated_at).toLocaleString('tr-TR') : '—'}
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default SignalScorecardPage;
